import { forwardRef } from "react";
import { Lock } from "@semo/icons";
import { cn } from "@semo/shared-ui";

/**
 * PartyAvatar Props
 */
export interface PartyAvatarProps {
  /** 이름 (이니셜 생성용) */
  name: string;
  /** 프로필 이미지 URL */
  imageUrl?: string | null;
  /** 추가 스타일 클래스 */
  className?: string;
  /** 텍스트 스타일 클래스 */
  textClassName?: string;
  /** 크기 프리셋 */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** 익명 여부 */
  isAnonymous?: boolean;
}

/**
 * 이름 기반 색상 팔레트 (VendorAvatar 파스텔톤과 동일)
 */
const COLORS = [
  // 세모 보라 계열 — 사람마다 밝기만 달리한다
  { bg: "#F3E8FF", text: "#5F16AB" },
  { bg: "#E6CEFF", text: "#47167A" },
  { bg: "#D3AAFF", text: "#47167A" },
  { bg: "#EBDDFB", text: "#5F16AB" },
] as const;

/**
 * 크기별 설정
 */
const SIZE_CONFIGS = {
  xs: { container: "w-6 h-6", text: "text-2xs" },
  sm: { container: "w-8 h-8", text: "text-xs" },
  md: { container: "w-10 h-10", text: "text-sm" },
  lg: { container: "w-12 h-12", text: "text-base" },
  xl: { container: "w-16 h-16", text: "text-xl" },
} as const;

/**
 * PartyAvatar - 채팅 상대방 아바타 컴포넌트
 *
 * 이미지가 있으면 이미지를 표시하고, 없으면 이름의 첫 글자로 이니셜을 표시합니다.
 * 이름을 기반으로 일관된 배경색이 적용됩니다.
 */
export const PartyAvatar = forwardRef<HTMLDivElement, PartyAvatarProps>(
  (
    { name, imageUrl, className, textClassName, size = "md", isAnonymous },
    ref,
  ) => {
    const sizeConfig = SIZE_CONFIGS[size];

    // 이미지가 있으면 이미지로 표시
    if (imageUrl) {
      return (
        <div
          ref={ref}
          data-slot="party-avatar"
          className={cn(
            "rounded-full overflow-hidden",
            sizeConfig.container,
            className,
          )}
        >
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover"
          />
        </div>
      );
    }

    // 익명 표시 (잠금 아이콘)
    if (isAnonymous) {
      const iconSize =
        size === "xs"
          ? 10
          : size === "sm"
            ? 14
            : size === "md"
              ? 20
              : size === "lg"
                ? 24
                : 28;
      return (
        <div
          ref={ref}
          data-slot="party-avatar"
          className={cn(
            "flex items-center justify-center",
            "text-white bg-slate-600",
            "rounded-full overflow-hidden",
            sizeConfig.container,
            className,
          )}
        >
          <Lock size={iconSize} strokeWidth={2.5} />
        </div>
      );
    }

    // 이름이 없는 경우 기본값 처리
    const safeName = name || "?";
    const firstChar = safeName.charAt(0);

    // 이름 기반 색상 선택 (일관성 유지)
    const colorIndex =
      safeName.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      COLORS.length;
    const color = COLORS[colorIndex] || COLORS[0];

    return (
      <div
        ref={ref}
        data-slot="party-avatar"
        className={cn(
          "flex items-center justify-center",
          "font-semibold",
          "rounded-full overflow-hidden",
          sizeConfig.container,
          className,
        )}
        style={{ backgroundColor: color.bg, color: color.text }}
      >
        <span className={cn(sizeConfig.text, textClassName)}>{firstChar}</span>
      </div>
    );
  },
);

PartyAvatar.displayName = "PartyAvatar";
